import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Spin, message, Tabs } from 'antd';
import { 
  AppstoreOutlined, 
  TeamOutlined, 
  UserOutlined, 
  BookOutlined,
  CalendarOutlined 
} from '@ant-design/icons';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import StudentStatsByYear from './StudentStatsByYear';
import './DashboardPage.css';

const { TabPane } = Tabs;

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface DashboardStats {
  courses: number;
  classes: number;
  students: number;
  teachers: number;
}

interface ChartData {
  name: string;
  count: number;
}

const DashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    courses: 0,
    classes: 0,
    students: 0,
    teachers: 0
  });
  const [classesData, setClassesData] = useState<ChartData[]>([]);
  const [studentsData, setStudentsData] = useState<ChartData[]>([]);

  // Lấy role từ localStorage
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const roleKey = (userInfo.roleKey || userInfo.RoleKey || '').toLowerCase();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async (): Promise<void> => {
    setLoading(true);
    try {
      const token = localStorage.getItem('quanly_truonghoc_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

  // Determine API base URL via central config
  const { buildAPIURL } = await import('../../config/apiConfig');
  const apiBase = buildAPIURL('');

      // Lấy thống kê tổng quan
      const [statsRes, classesRes, studentsRes] = await Promise.all([
  fetch(`${apiBase}/dashboard/statistics`, { headers }),
  fetch(`${apiBase}/dashboard/classes-by-program`, { headers }),
  fetch(`${apiBase}/dashboard/students-by-major`, { headers })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (classesRes.ok) {
        const classesData = await classesRes.json();
        setClassesData(classesData);
      }

      if (studentsRes.ok) {
        const studentsData = await studentsRes.json();
        setStudentsData(studentsData);
      }

    } catch (error) {
      console.error('Lỗi khi tải dữ liệu dashboard:', error);
      message.error('Không thể tải dữ liệu dashboard');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24, color: '#1890ff' }}>
        Chào mừng trở lại! 👋
      </h2>
      
      <div style={{ 
        background: 'linear-gradient(180deg, #065dc0 88%, #e35f01ff 2%)',
        borderRadius: 8,
        padding: 24,
        marginBottom: 24,
        color: 'white'
      }}>
        <h3 style={{ margin: 0, color: 'white' }}>
          Hệ thống Quản lý Trường học - Cao đẳng Y tế Thái Bình
        </h3>
      </div>

      <Tabs defaultActiveKey="1" type="card" size="large">
        <TabPane 
          tab={
            <span>
              <AppstoreOutlined />
              Tổng quan
            </span>
          } 
          key="1"
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: 50 }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {/* Thống kê tổng quan */}
              <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={12} md={6}>
                  <Card
                    style={{
                      background: 'linear-gradient(135deg, #e3eafc 0%, #b6c6e6 100%)',
                      border: 'none',
                      boxShadow: '0 4px 18px 0 rgba(59,130,246,0.10)'
                    }}
                  >
                    <Statistic
                      title="Khóa học"
                      value={stats.courses}
                      prefix={<BookOutlined style={{ color: '#2563eb' }} />}
                      valueStyle={{ color: '#2563eb', fontWeight: 600 }}
                    />
                  </Card>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <Card
                    style={{
                      background: 'linear-gradient(135deg, #fffbe6 0%, #ffe58f 100%)',
                      border: 'none',
                      boxShadow: '0 4px 18px 0 rgba(251,191,36,0.10)'
                    }}
                  >
                    <Statistic
                      title="Lớp học"
                      value={stats.classes}
                      prefix={<AppstoreOutlined style={{ color: '#d97706' }} />}
                      valueStyle={{ color: '#d97706', fontWeight: 600 }}
                    />
                  </Card>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <Card
                    style={{
                      background: 'linear-gradient(135deg, #e6fffa 0%, #b2f5ea 100%)',
                      border: 'none',
                      boxShadow: '0 4px 18px 0 rgba(34,197,94,0.10)'
                    }}
                  >
                    <Statistic
                      title="Sinh viên"
                      value={stats.students}
                      prefix={<UserOutlined style={{ color: '#059669' }} />}
                      valueStyle={{ color: '#059669', fontWeight: 600 }}
                    />
                  </Card>
                </Col>
                
                {/* Hiển thị box Giảng viên cho tất cả người dùng */}
                <Col xs={24} sm={12} md={6}>
                  <Card
                    style={{
                      background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                      border: 'none',
                      boxShadow: '0 4px 18px 0 rgba(168,85,247,0.10)'
                    }}
                  >
                    <Statistic
                      title="Giảng viên"
                      value={stats.teachers}
                      prefix={<TeamOutlined style={{ color: '#9333ea' }} />}
                      valueStyle={{ color: '#9333ea', fontWeight: 600 }}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Biểu đồ thống kê */}
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={14}>
                  <Card title="Biểu đồ số lớp học theo khóa học" style={{ height: 400 }}>
                    {classesData.length > 0 ? (
                      <Bar
                        data={{
                          labels: classesData.map(item => item.name),
                          datasets: [{
                            label: 'Số lớp học',
                            data: classesData.map(item => item.count),
                            backgroundColor: '#1890ff',
                            borderColor: '#1890ff',
                            borderWidth: 1
                          }]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            x: {
                              title: {
                                display: true,
                                text: 'Khóa học'
                              }
                            },
                            y: {
                              beginAtZero: true,
                              title: {
                                display: true,
                                text: 'Số lớp học'
                              }
                            }
                          }
                        }}
                        height={300}
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: 50 }}>
                        Không có dữ liệu
                      </div>
                    )}
                  </Card>
                </Col>

                <Col xs={24} lg={10}>
                  <Card title="Phân bố sinh viên theo ngành" style={{ height: 400 }}>
                    {studentsData.length > 0 ? (
                      <Pie
                        data={{
                          labels: studentsData.map(item => item.name),
                          datasets: [{
                            data: studentsData.map(item => item.count),
                            backgroundColor: [
                              '#FF6384',
                              '#36A2EB',
                              '#FFCE56',
                              '#4BC0C0',
                              '#9966FF',
                              '#FF9F40'
                            ],
                            borderWidth: 1
                          }]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'bottom' as const
                            }
                          }
                        }}
                        height={300}
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: 50 }}>
                        Không có dữ liệu
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </TabPane>

        <TabPane 
          tab={
            <span>
              <CalendarOutlined />
              Thống kê theo năm
            </span>
          } 
          key="2"
        >
          <StudentStatsByYear />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default DashboardPage; 